/*
 * Configuration object for bookmaker.
 * @typedef {Object} BookmakerConfiguration
 * @property {string} name - prettified (readable) bookmaker name used in CSS-class naming
 * @property {string} apiName - bookmaker name used in API calls
 * @property {string} feed - unique bookmaker name, used for identifying bookmaker.
 * Also used as bookmaker name in diffusion topics creation.
 * @property {string} dynamicDataKey - bookmaker name, used for identifying bookmaker in bookmakers
 * dynamic data "/stubsapi/config" which contains dynamic info from bookmaker
 * (registration offer, promo code, etc.)
 * @property {string} displayName - string, which displayed to users as bookmaker name.
 * Used in 2 modals: during selection current bookmaker to show which bookmaker will be used for
 * all prices and after login in not current bookmaker, to help user use prices for this bookmaker
 * @property {string} analyticsName - bookmaker name which sends to analytics (segmentIO)
 * to track user actions
 * @property {boolean} availableOnPhone - flag which describes whether or not bookmaker
 * is available on mobile phone devices
 * @property {string} cookieName - cookie name set on bookmaker login
 * @property {object} theme - theme configuration for bookmaker
 * @property {string} theme.clearBetIconName - icon name for clearing bet
 * @property {string} theme.clearStakeIconName - icon name for clearing bet stake
 * @property {string} theme.spinnerIconName - icon name for spinner
 * @property {string} theme.checkboxIconType - icon type for checkbox
 * @property {string} loginType - type of bookmaker login. One of: 'form', 'iframe', 'window'
 * @property {boolean} isTopUpIntegrated - describes if bookmakers account can be topuped
 * with betslip API
 * @property {boolean} isExternalBetHistory - describes if bookmaker provides external url
 * for account bet history page
 * @property {boolean} hasLogoOnAccountHeader - describes if bookmaker has its logo in
 * account iframe
 * @property {boolean} isExternalAccount - describes if bookmaker provides iframe for account page,
 * which includes account info and bet history functionality
 * @property {boolean} isExternalLogout - describes if bookmaker provides external logout url,
 * @property {boolean} accountType - type of bookmaker account. One of: 'form', 'iframe', 'window'
 * @property {boolean} hasAccountTabs - describes if bookmaker provides integrated account tabs
 * @property {boolean} freeBetsEnabled - describes if free bets enabled for bookmaker
 * @property {boolean} containsTermsInLoginIframe - describes if bookmaker contains terms
 * and conditions in login iframe
 * @property {boolean} containsTermsInTopUpIframe - describes if bookmaker contains terms
 * and conditions intopupiframe
 */

import { bookmakerNames } from "../utils/constants/bookmakers";

const {
    coral,
    ladbrokes,
    bet365,
    betfair,
    paddyPower,
    skybet,
    williamHill,
} = bookmakerNames

function generateConfig(cookieNames) {
    return [
        {
            name: coral,
            apiName: 'coral',
            feed: 'CORAL',
            dynamicDataKey: 'coral',
            displayName: 'Coral',
            analyticsName: 'Coral',
            availableOnPhone: true,
            cookieName: cookieNames.coral,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'iframe',
            loginDuration: 3600,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: true,
            isExternalAccount: true,
            isExternalLogout: false,
            accountType: 'iframe',
            hasAccountTabs: true,
            freeBetsEnabled: true,
            topUpBookmakerName: 'www.coral.co.uk',
            topUpBookmakerUrl: 'https://www.coral.co.uk',
        },
        {
            name: ladbrokes,
            apiName: 'ladbrokes',
            dynamicDataKey: 'ladbrokes',
            feed: 'LADB',
            displayName: 'Ladbrokes',
            analyticsName: 'Ladbrokes',
            availableOnPhone: true,
            cookieName: cookieNames.ladbrokes,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'iframe',
            loginDuration: 3600,
            containsTermsInLoginIframe: true,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: false,
            isExternalAccount: true,
            isExternalLogout: false,
            accountType: 'iframe',
            hasAccountTabs: true,
            containsTermsInTopUpIframe: true,
            freeBetsEnabled: true,
        },
        {
            name: paddyPower,
            apiName: 'paddypower',
            dynamicDataKey: 'paddypower',
            feed: 'PPWR',
            displayName: 'Paddy Power',
            analyticsName: 'Paddy Power',
            availableOnPhone: true,
            cookieName: cookieNames.paddyPower,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'window',
            loginDuration: 28800,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: true,
            isExternalAccount: false,
            isExternalLogout: true,
            accountType: 'form',
            hasAccountTabs: true,
            freeBetsEnabled: true,
        },
        {
            name: betfair,
            apiName: 'betfair',
            dynamicDataKey: 'betfair',
            feed: 'BETFAIR',
            displayName: 'Betfair',
            analyticsName: 'Betfair',
            availableOnPhone: true,
            cookieName: cookieNames.betfair,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'window',
            loginDuration: 28800,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: true,
            isExternalAccount: false,
            isExternalLogout: true,
            accountType: 'form',
            hasAccountTabs: true,
            freeBetsEnabled: true,
        },
        {
            name: williamHill,
            apiName: 'williamhill',
            dynamicDataKey: 'williamHill',
            feed: 'WH_OXI',
            displayName: 'William Hill',
            analyticsName: 'William Hill',
            availableOnPhone: true,
            cookieName: cookieNames.williamHill,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'form',
            loginDuration: 28800,
            containsTermsInLoginIframe: true,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: true,
            isExternalAccount: false,
            isExternalLogout: false,
            accountType: 'form',
            hasAccountTabs: true,
            freeBetsEnabled: true,
            topUpBookmakerName: 'www.williamhill.com',
            topUpBookmakerUrl: 'http://www.williamhill.com',
        },
        {
            name: bet365,
            apiName: 'bet365',
            dynamicDataKey: 'bet365',
            feed: 'BET365',
            displayName: 'Bet365',
            analyticsName: 'bet365',
            availableOnPhone: true,
            cookieName: cookieNames.bet365,
            theme: {
                clearBetIconName: 'clear-bet-reverse',
                clearStakeIconName: 'clear-stake-reverse',
                spinnerIconName: 'spinner-reverse',
                checkboxIconType: 'color-reverse',
            },
            loginType: 'iframe',
            loginDuration: 3600,
            isTopUpIntegrated: true,
            isExternalBetHistory: false,
            hasLogoOnAccountHeader: true,
            isExternalAccount: false,
            isExternalLogout: false,
            accountType: 'form',
            hasAccountTabs: true,
            freeBetsEnabled: false,
            topUpBookmakerName: 'www.bet365.com',
            topUpBookmakerUrl: 'https://www.bet365.com',
        },
        {
            name: skybet,
            apiName: 'skybet',
            feed: 'SURREY',
            dynamicDataKey: 'skybet',
            displayName: 'Sky Bet',
            analyticsName: 'SkyBet',
            availableOnPhone: true,
            cookieName: cookieNames.skybet,
            theme: {
                clearBetIconName: 'clear-bet',
                clearStakeIconName: 'clear-stake',
                spinnerIconName: 'spinner',
                checkboxIconType: 'color',
            },
            loginType: 'window',
            loginDuration: 3600,
            isTopUpIntegrated: false,
            isExternalBetHistory: true,
            hasLogoOnAccountHeader: true,
            isExternalAccount: true,
            isExternalLogout: true,
            accountType: 'window',
            hasAccountTabs: false,
            freeBetsEnabled: true,
        },
    ]
}

export default function getBookmakersConfig() {
    let cookieNames = {
        [coral]: 'c-c',
        [ladbrokes]: 'lb-c',
        [paddyPower]: 'pp-c',
        [williamHill]: 'wh-c',
        [bet365]: 'bet365-c',
        [betfair]: 'bf-c',
        [skybet]: 'sb-c',
    }

    if (process.env.NODE_ENV === 'local') {
        cookieNames = {
            [coral]: 'coral-auth',
            [ladbrokes]: 'ladbrokes-auth',
            [paddyPower]: 'paddypower-auth',
            [williamHill]: 'wh-c',
            [bet365]: 'bet365-auth',
            [betfair]: 'betfair-auth',
            [skybet]: 'skybet-auth',
        }
    }

    return generateConfig(cookieNames)
}



// WEBPACK FOOTER //
// /src/js/common/bookmakersList/bookmakers.js